import { Role } from 'auth';
import dayjs, { Dayjs } from 'dayjs';
import omit from 'lodash/omit';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useOutletContext } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { ArchiveSwitch } from '@/modules/common/components/ArchiveSwitch.tsx';
import { FiltersGroup } from '@/modules/common/components/FiltersGroup.tsx';
import RegisterClassesSelectPaginate from '@/modules/common/components/RegisterClassesSelectPaginate.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { DistributionMore } from '@/modules/issuer-dashboard/components/distribution-dashboard/DistributionMore.tsx';
import { DividendOperationInfoModal } from '@/modules/issuer-dashboard/components/distribution-dashboard/DividendOperationInfoModal.tsx';
import { IssuerAddDistributionModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddDistributionModal.tsx';
import { useIssuerDividendEventInfiniteListQuery } from '@/modules/issuer-dashboard/queries/dividends.tsx';
import {
  BodyCell,
  Button,
  FilterItem,
  ModalProviderContext,
  PageLayout,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  getSelectOptionByEnum,
} from 'ui';
import { useIsMobile, useSaveState } from 'ui/hooks';
import { goodFormatUSDCurrency, goodFormatUSDCurrencyLowPrecision } from 'ui/utils';
import { DividendStatus, GetDividendEventListV1SrDividendEventsGetParams } from 'api';

const IssuerDistributionsEvents = () => {
  const [isDeleted, setIsDeleted] = useState<boolean | undefined>();
  return (
    <PageLayout
      title="Events"
      headerAction={<DistributionsHeaderAction isDeleted={isDeleted} setIsDeleted={setIsDeleted} />}
      contentClassName="px-0 py-2"
      classNameTitle="flex-row justify-between items-center"
    >
      <DistributionsEventsBody isDeleted={isDeleted} />
    </PageLayout>
  );
};

type TypeHead = 'ID' | 'Name' | 'Status' | 'Class' | 'Date' | '$ per share/unit' | 'Total' | '';

interface DistributionsHeaderActionProps {
  isDeleted?: boolean;
  setIsDeleted?: Dispatch<SetStateAction<boolean | undefined>>;
}

export const DistributionsHeaderAction = ({
  isDeleted,
  setIsDeleted,
}: DistributionsHeaderActionProps) => {
  const { registerId, registerCountry } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const { openModal } = useContext(ModalProviderContext);

  const handleOpenDistributionModal = useCallback(() => {
    openModal(IssuerAddDistributionModal, {
      registerCountry,
      register_id: Number(registerId),
    });
  }, [openModal, registerId, registerCountry]);

  return (
    <div className="flex gap-4">
      {!!setIsDeleted && (
        <ArchiveSwitch
          active={!!isDeleted}
          onChange={active => setIsDeleted(active || undefined)}
        />
      )}
      <Button className="w-[190px]" onClick={handleOpenDistributionModal}>
        Add new {getRegisterTypeStrings('dividend')}
      </Button>
    </div>
  );
};

export interface DistributionsEventsBodyProps {
  rightBlock?: ReactNode;
  hideChooseRegisterClass?: boolean;
  register_class_id?: number;
  isDeleted?: boolean;
}

export const DistributionsEventsBody = ({
  rightBlock,
  hideChooseRegisterClass = false,
  register_class_id,
  isDeleted,
}: DistributionsEventsBodyProps) => {
  const { isMobile } = useIsMobile();
  const { openModal } = useContext(ModalProviderContext);
  const { registerId, registerCountry } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const [filters, setFilters] = useSaveState<GetDividendEventListV1SrDividendEventsGetParams>(
    {
      register_class_id,
    },
    `filters-distributions-${registerId}`,
  );

  const {
    data: dividendEventList,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isError,
  } = useIssuerDividendEventInfiniteListQuery(
    {
      page: 1,
      page_size: 30,
      ...filters,
      register_class_id: register_class_id || filters?.register_class_id,
      is_deleted: isDeleted,
    },
    {},
  );

  const headItems = useMemo<TableHeadColumn<TypeHead>[]>(() => {
    let items: TableHeadColumn<TypeHead>[] = [
      { title: 'ID', className: '' },
      { title: 'Name', className: '' },
      { title: 'Status', className: '' },
      { title: 'Class', className: '' },
      { title: 'Date', className: '' },
      { title: '$ per share/unit', className: '' },
      { title: 'Total', className: 'min-w-[140px]' },
      { title: '', className: '' },
    ];

    if (hideChooseRegisterClass) {
      items = items.filter(item => item.title !== 'Class');
    }

    return items;
  }, [hideChooseRegisterClass]);

  const handleOpenFull = useCallback(
    (id: number) => () => {
      openModal(DividendOperationInfoModal, {
        id,
        registerId,
        registerCountry,
        isDeleted,
      });
    },
    [isDeleted, openModal, registerCountry, registerId],
  );

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    if (!dividendEventList) {
      return [];
    }

    let result: TableBodyColumn[] = [];

    dividendEventList.pages.forEach(page => {
      result = result.concat(
        page.data?.map(data => {
          let body: BodyCell[] = [
            {
              cell: (
                <span className="text-sm leading-[22px] text-[#0369A1] font-[500]">#{data.id}</span>
              ),
            },
            {
              cell: (
                <span className="text-sm leading-[22px] text-[#0369A1] font-[500]">
                  {data.name}
                </span>
              ),
            },
            {
              cell: (
                <span>
                  <ActivityStatusBadge status={data.status || ''} />
                </span>
              ),
            },
            {
              cell: <span>{data.register_class?.class_name ?? '-'}</span>,
            },
            {
              cell: <div className="">{dateToPrint(data.date, DateVariant.Table) || '-'}</div>,
            },
            {
              cell: <span>{goodFormatUSDCurrency(data.amount_per_share ?? 0)}</span>,
            },
            {
              cell: <span>{goodFormatUSDCurrencyLowPrecision(data.total_amount ?? 0)}</span>,
            },
            {
              cell: (
                <DistributionMore
                  item={data}
                  registerId={registerId}
                  registerCountry={registerCountry}
                  isDeleted={isDeleted}
                />
              ),
            },
          ];

          if (hideChooseRegisterClass) {
            body = body.filter((_, i) => i !== 2); //remove class
          }

          return {
            cells: body,
            onClick: handleOpenFull(data.id),
          };
        }),
      );
    });

    return result;
  }, [
    dividendEventList,
    handleOpenFull,
    hideChooseRegisterClass,
    isDeleted,
    registerCountry,
    registerId,
  ]);

  return (
    <div className="flex flex-col gap-2 w-full">
      <div
        data-mobile-actions
        data-mobile-toolbar
        className="min-w-0 flex flex-wrap sm:flex-nowrap items-start justify-between gap-4 w-full px-3 sm:px-6"
      >
        <FiltersPanelEvents
          setFilters={setFilters}
          filters={filters}
          registerId={registerId}
          isFetching={isFetching}
          hideChooseRegisterClass={hideChooseRegisterClass}
        />
        {rightBlock}
      </div>
      <Table
        className="my-0"
        classNameHead="border-t-0"
        withRounded={isMobile}
        withBorder={true}
        withBorderX={false}
        headItems={headItems}
        items={bodyItems}
        isLoading={isLoading}
        isError={isError}
        hasNextPageInfinite={hasNextPage}
        fetchNextPageInfinite={fetchNextPage}
        blockIdInfinite="scrollable"
        mobileFirstCellShowInHeadLeft={true}
        mobileLastCellShowInHeadRight={true}
      />
    </div>
  );
};

interface FiltersPanelEventsProps {
  filters: GetDividendEventListV1SrDividendEventsGetParams;
  setFilters: Dispatch<SetStateAction<GetDividendEventListV1SrDividendEventsGetParams>>;
  registerId: string;
  isFetching: boolean;
  hideChooseRegisterClass?: boolean;
  forBuyBack?: boolean;
  forTransfers?: boolean;
}

export const FiltersPanelEvents = ({
  filters,
  setFilters,
  registerId,
  isFetching,
}: FiltersPanelEventsProps) => {
  const handleChangeStatus = useCallback(
    (status: DividendStatus | null) => {
      setFilters(filters => (status ? { ...filters, status } : omit(filters, 'status')));
    },
    [setFilters],
  );

  const dateValue =
    filters.date_from && filters.date_to
      ? ([dayjs(filters.date_from), dayjs(filters.date_to)] as [Dayjs, Dayjs])
      : undefined;

  const handleChangeDate = useCallback(
    (date: [Dayjs, Dayjs] | null) => {
      setFilters(filters => {
        return date
          ? {
              ...filters,
              date_from: date[0].toISOString(),
              date_to: date[1].endOf('d').toISOString(),
            }
          : omit(filters, ['date_from', 'date_to']);
      });
    },
    [setFilters],
  );

  const handleChangeRegisterClass = useCallback(
    (registerClassId: number | null) => {
      setFilters(filters => {
        return registerClassId
          ? {
              ...filters,
              register_class_id: +registerClassId,
            }
          : omit(filters, ['register_class_id']);
      });
    },
    [setFilters],
  );

  return (
    <FiltersGroup variant="inline" className="flex gap-2 items-center flex-wrap w-full">
      <FilterItem
        placeholder="Date"
        type="date"
        onChange={handleChangeDate}
        value={dateValue}
        className="rounded-lg"
      />

      <RegisterClassesSelectPaginate
        registerId={+registerId}
        onChange={item => {
          handleChangeRegisterClass(item?.value.id || null);
        }}
        value={filters?.register_class_id}
        isMulti={false}
        placeholder="Class"
        label=""
        className="flex items-center border px-2 rounded-lg gap-2 w-full font-[Inter] text-sm leading-[22px] max-w-[350px] min-w-[200px]"
        classNameSelect="w-full"
        formFieldTitle=""
        showBorderBottom={false}
        withBorder={false}
        clearable={true}
      />

      <FilterItem
        placeholder="Status"
        type="select"
        value={filters.status}
        options={getSelectOptionByEnum<DividendStatus>(DividendStatus)}
        onChange={handleChangeStatus}
        className="rounded-lg"
        classes={{
          label: 'w-[85px] shrink-0',
          element: 'w-full min-w-[150px] [&>div>div]:gap-1 [&>div>div]:md:!gap-3',
        }}
      />

      {isFetching && <Spinner size="md" variant="primary" />}
    </FiltersGroup>
  );
};

export default withPrivateAccess(IssuerDistributionsEvents, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
